import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { entero } from '../lib/formato.js';
import { opciones, type Opcion } from '../lib/opciones.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { Cargando, Error as AvisoError, Toast, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Embarque a sitios.
 *
 * UNA GUIA POR SITIO: lo que viaja junto llega junto. Una guia por equipo
 * multiplica por quinientos el trabajo de la paqueteria y de quien recibe.
 *
 * El embarque solo aplica en modalidad en facilidad: en sitio destino el
 * equipo ya esta donde se queda, y esta pantalla no ofrece nada de esos
 * proyectos porque no hay nada que mandar.
 */

interface Grupo {
  sitio_id: string;
  sitio: string;
  sitio_nombre: string;
  ciudad: string | null;
  equipos: number;
  ids: string[];
  series: string[];
}

interface Plan {
  modalidad: { modalidad: string | null };
  por_sitio: Grupo[];
  sin_sitio: { equipos: number };
}

export function Embarque(): JSX.Element {
  const [proyectos, setProyectos] = useState<Opcion[]>([]);
  const [paqueterias, setPaqueterias] = useState<Opcion[]>([]);
  const [proyecto, setProyecto] = useState('');
  const [paqueteria, setPaqueteria] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [numeros, setNumeros] = useState<Record<string, string>>({});
  const [elegidos, setElegidos] = useState<Set<string>>(new Set());
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const { puede } = useSesion();

  useEffect(() => {
    opciones('proyectos').then(setProyectos).catch(setError);
    opciones('paqueterias').then(setPaqueterias).catch(setError);
  }, []);

  const cargar = useCallback(() => {
    if (proyecto === '') {
      setPlan(null);
      return;
    }
    setPlan(null);
    api
      .obtener<Plan>(`/v1/staging/embarque?proyecto_id=${proyecto}`)
      .then(({ datos }) => {
        setPlan(datos);
        /* Todos los sitios elegidos por omision: lo normal es embarcar lo que
           esta listo, y des-elegir es menos trabajo que elegir uno por uno. */
        setElegidos(new Set(datos.por_sitio.map((g) => g.sitio_id)));
        setNumeros({});
      })
      .catch(setError);
  }, [proyecto]);

  useEffect(cargar, [cargar]);

  const aEmbarcar = (plan?.por_sitio ?? []).filter((g) => elegidos.has(g.sitio_id));
  const faltanNumeros = aEmbarcar.filter((g) => (numeros[g.sitio_id] ?? '').trim().length < 4);
  const listo =
    paqueteria !== '' && aEmbarcar.length > 0 && faltanNumeros.length === 0 && !guardando;

  async function embarcar(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      const { datos } = await api.enviar<{ guias: { folio: string; equipos: number }[] }>(
        '/v1/staging/embarque',
        {
          proyecto_id: proyecto,
          paqueteria_id: paqueteria,
          guias: aEmbarcar.map((g) => ({
            sitio_id: g.sitio_id,
            numero_guia: (numeros[g.sitio_id] ?? '').trim(),
            equipos: g.ids,
          })),
        },
      );
      const equipos = datos.guias.reduce((a, x) => a + x.equipos, 0);
      setAviso(
        `${datos.guias.length} ${datos.guias.length === 1 ? 'guia' : 'guias'} generadas con ${entero(equipos)} equipos.`,
      );
      cargar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      <Pagehead />

      <div className="grid campos" style={{ marginBottom: 12 }}>
        <label className="campo">
          <span>
            Proyecto <b>*</b>
          </span>
          <select
            value={proyecto}
            onChange={(ev) => setProyecto(ev.target.value)}
            data-prueba="proyecto-embarque"
          >
            <option value="">Elige…</option>
            {proyectos
              .filter((p) => p.activo)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.etiqueta}
                </option>
              ))}
          </select>
        </label>

        <label className="campo">
          <span>
            Paqueteria <b>*</b>
          </span>
          <select
            value={paqueteria}
            onChange={(ev) => setPaqueteria(ev.target.value)}
            data-prueba="paqueteria"
          >
            <option value="">Elige…</option>
            {paqueterias
              .filter((p) => p.activo)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.etiqueta}
                </option>
              ))}
          </select>
        </label>
      </div>

      {error !== null && <AvisoError error={error} />}

      {proyecto === '' ? (
        <div className="card">
          <Vacio
            mensaje="Elige un proyecto"
            ayuda="Se muestra lo que esta LISTO para embarcar, agrupado por sitio."
            icono="🚚"
          />
        </div>
      ) : plan === null ? (
        <Cargando que="el plan de embarque" />
      ) : (
        <>
          {/* La advertencia, antes de la tabla: sin esto se embarca creyendo
              que ya salio todo y los que no tienen destino se quedan. */}
          {plan.sin_sitio.equipos > 0 && (
            <div className="card" style={{ marginBottom: 12 }} data-prueba="aviso-sin-sitio">
              <b style={{ color: 'var(--warn)' }}>
                {entero(plan.sin_sitio.equipos)} equipos cargados no tienen sitio asignado
              </b>
              <div className="muted">
                No se pueden embarcar hasta que tengan destino.{' '}
                <Link to="/equipos?sin=sitio_id">Asignarselo en el padron</Link>.
              </div>
            </div>
          )}

          {plan.por_sitio.length === 0 ? (
            <div className="card">
              <Vacio
                mensaje="No hay nada listo para embarcar"
                ayuda={
                  plan.modalidad.modalidad === 'STG_SITIO'
                    ? 'Este proyecto es en sitio destino: el equipo ya esta donde se queda y no hay embarque.'
                    : 'Un equipo esta listo cuando su carga se valido y tiene sitio asignado.'
                }
                icono="🚚"
              />
            </div>
          ) : (
            <>
              <div className="card p0">
                <table data-prueba="tabla-embarque">
                  <thead>
                    <tr>
                      <th />
                      <th>Sitio</th>
                      <th className="num">Equipos</th>
                      <th>Numero de guia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.por_sitio.map((g) => (
                      <tr key={g.sitio_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={elegidos.has(g.sitio_id)}
                            onChange={(ev) => {
                              const copia = new Set(elegidos);
                              if (ev.target.checked) copia.add(g.sitio_id);
                              else copia.delete(g.sitio_id);
                              setElegidos(copia);
                            }}
                            data-prueba={`elige-${g.sitio}`}
                          />
                        </td>
                        <td>
                          <span className="folio">{g.sitio}</span>
                          <div className="muted" style={{ fontSize: 11 }}>
                            {g.sitio_nombre}
                            {g.ciudad !== null && ` · ${g.ciudad}`}
                          </div>
                        </td>
                        <td className="num">
                          {entero(g.equipos)}
                          <div className="muted" style={{ fontSize: 11 }}>
                            {g.series.slice(0, 3).join(', ')}
                            {g.series.length > 3 && ` +${g.series.length - 3}`}
                          </div>
                        </td>
                        <td>
                          <input
                            value={numeros[g.sitio_id] ?? ''}
                            onChange={(ev) =>
                              setNumeros({ ...numeros, [g.sitio_id]: ev.target.value })
                            }
                            placeholder="El que dio la paqueteria"
                            disabled={!elegidos.has(g.sitio_id)}
                            data-prueba={`numero-${g.sitio}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {puede('MOD_STAGING', 'escribir') && (
                <div className="fila wrap" style={{ marginTop: 12, gap: 12 }}>
                  <span>
                    <b>{aEmbarcar.length}</b> {aEmbarcar.length === 1 ? 'guia' : 'guias'} con{' '}
                    <b>{entero(aEmbarcar.reduce((a, g) => a + g.equipos, 0))}</b> equipos
                  </span>
                  {faltanNumeros.length > 0 && (
                    <span className="muted">
                      Falta el numero de guia de {faltanNumeros.length}{' '}
                      {faltanNumeros.length === 1 ? 'sitio' : 'sitios'}.
                    </span>
                  )}
                  <span className="crece" />
                  <button
                    type="button"
                    className="btn pri"
                    disabled={!listo}
                    onClick={() => void embarcar()}
                    data-prueba="generar-guias"
                  >
                    {guardando ? 'Generando…' : 'Generar guias'}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {aviso !== null && <Toast mensaje={aviso} />}
    </>
  );
}
